import { FC, useEffect, useRef, useState } from "react";
import useTrackingEntityUsers from "../hooks/useTrackingEntityUsers";
import type { TrackableSurface } from "../utils/eventNames";
import { type Period } from "../utils/period";
import Pagination from "src/components/Pagination/Pagination";

interface Props {
	surface: TrackableSurface;
	entityId: string;
	title: string;
	period: Period;
	onClose: () => void;
}

const PAGE_SIZE = 25;

const EntityAudiencePanel: FC<Props> = ({
	surface,
	entityId,
	title,
	period,
	onClose
}) => {
	const [page, setPage] = useState(1);
	const query = useTrackingEntityUsers({
		surface,
		entityId,
		from: period.from,
		to: period.to,
		page,
		pageSize: PAGE_SIZE
	});

	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const previouslyFocusedRef = useRef<HTMLElement | null>(null);

	// Keep the latest onClose in a ref so the keydown listener below can
	// stay subscribed once for the panel's lifetime — without this, an
	// inline onClose from the parent would re-run the focus/listener
	// effects on every parent render, stealing focus mid-interaction.
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	// Mount-only: capture the previously focused element, move focus into
	// the dialog, and restore focus on unmount.
	useEffect(() => {
		previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
		closeButtonRef.current?.focus();
		return () => {
			previouslyFocusedRef.current?.focus?.();
		};
	}, []);

	// Mount-only: Escape-to-close + Tab/Shift+Tab focus trap. Reads
	// onCloseRef so it never needs to re-subscribe.
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				onCloseRef.current();
				return;
			}
			if (e.key !== "Tab") return;
			const root = dialogRef.current;
			if (!root) return;
			const tabbables = Array.from(
				root.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
				)
			).filter((el) => !el.hasAttribute("aria-hidden"));
			if (tabbables.length === 0) return;
			const first = tabbables[0];
			const last = tabbables[tabbables.length - 1];
			const active = document.activeElement;
			if (e.shiftKey && active === first) {
				e.preventDefault();
				last?.focus();
			} else if (!e.shiftKey && active === last) {
				e.preventDefault();
				first?.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
		};
	}, []);

	const users = query.data?.users ?? [];
	const total = query.data?.total ?? 0;

	return (
		<div
			ref={dialogRef}
			className="fixed inset-0 z-40 flex"
			role="dialog"
			aria-modal="true"
			aria-label={`Audience for ${title}`}
		>
			<div
				className="flex-1 bg-black/30"
				onClick={onClose}
				aria-hidden="true"
			/>
			<aside className="w-full max-w-xl h-full bg-white border-l border-border shadow-xl overflow-y-auto p-6">
				<div className="flex items-start justify-between mb-4">
					<div>
						<h2 className="font-heading text-lg font-semibold text-foreground">
							{title}
						</h2>
						<p className="text-sm text-muted-foreground">
							{total.toLocaleString()} {total === 1 ? "person" : "people"} ·{" "}
							{period.label}
						</p>
					</div>
					<button
						type="button"
						ref={closeButtonRef}
						onClick={onClose}
						className="text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:rounded"
					>
						Close
					</button>
				</div>

				<div aria-live="polite" aria-busy={query.isLoading}>
					{query.isLoading ? (
						<div className="h-32 animate-pulse bg-zinc-100 rounded" />
					) : query.isError ? (
						<p role="alert" className="text-sm text-rose-600">
							Failed to load audience.
						</p>
					) : users.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No recorded views for this item in the selected period.
						</p>
					) : (
						<>
							<ul className="divide-y divide-border">
								{users.map((u) => (
									<li
										key={u.user_id}
										className="py-2.5 flex items-center justify-between gap-3"
									>
										<div className="min-w-0">
											<p className="text-sm text-foreground truncate">
												{u.full_name ?? u.user_id}
											</p>
											<p className="text-xs text-muted-foreground truncate">
												{u.job_title ?? ""}
												{u.org_team_level_one
													? ` · ${u.org_team_level_one}`
													: ""}
											</p>
										</div>
										<div className="flex items-center gap-2 shrink-0">
											{u.seen ? (
												<span className="text-xs px-1.5 py-0.5 rounded bg-subtle text-muted-foreground">
													Seen
												</span>
											) : null}
											{u.clicked ? (
												<span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">
													Clicked
												</span>
											) : null}
											<span className="text-xs text-muted-foreground">
												{u.last_seen.slice(0, 10)}
											</span>
										</div>
									</li>
								))}
							</ul>
							{total > PAGE_SIZE ? (
								<div className="pt-3">
									<Pagination
										page={page}
										pageSize={PAGE_SIZE}
										totalCount={total}
										onPageChange={setPage}
										itemLabel="people"
									/>
								</div>
							) : null}
						</>
					)}
				</div>
			</aside>
		</div>
	);
};

export default EntityAudiencePanel;
